import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../state/NotificationsContext";

function timeLabel(iso: string): string {
  const date = new Date(iso);
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  return sameDay
    ? date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/**
 * Compact notification entry point: a 44 px bell icon button with an unread
 * badge; opens an inline panel listing the recent feed. On phone widths it
 * lives inside the mobile top bar's trailing slot; on desktop it renders at
 * the top of the app content.
 */
export function NotificationBell() {
  // G-160: a web-order alert opens that order on Web orders.
  const navigate = useNavigate();
  const { notifications, unreadCount, markRead, markAllRead, sync } =
    useNotifications();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onOutsideClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onOutsideClick);
    return () => document.removeEventListener("mousedown", onOutsideClick);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) void sync();
  };

  return (
    <div ref={rootRef} className="bell-root">
      <button
        type="button"
        className="icon-button"
        onClick={toggle}
        aria-expanded={open}
        aria-label={
          unreadCount > 0
            ? `Notifications (${unreadCount} unread)`
            : "Notifications"
        }
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
        {unreadCount > 0 ? (
          <span className="icon-button-badge" aria-hidden="true">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className="card"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            width: "min(420px, 92vw)",
            maxHeight: "60vh",
            overflowY: "auto",
            zIndex: 30,
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-2)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
          }}
        >
          <div className="day-shifts-row-head">
            <strong>Notifications</strong>
            {unreadCount > 0 ? (
              <button
                type="button"
                className="button plain"
                onClick={() => void markAllRead()}
              >
                Mark all read
              </button>
            ) : null}
          </div>

          {notifications.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>
              Nothing yet.
            </p>
          ) : (
            notifications.map((notification) => (
              <button
                key={notification.id}
                type="button"
                onClick={() => {
                  if (!notification.readAt) void markRead(notification.id);
                  const orderId = notification.kind.startsWith("SHOPIFY_")
                    ? notification.payload?.orderId
                    : undefined;
                  if (orderId) {
                    setOpen(false);
                    navigate(`/app/shopify-orders?order=${encodeURIComponent(orderId)}`);
                  } else if (
                    notification.kind === "ORDER_RECIPE_CHANGED" &&
                    notification.payload?.orderId
                  ) {
                    setOpen(false);
                    navigate(`/app/orders/build/${encodeURIComponent(notification.payload.orderId)}`);
                  }
                }}
                style={{
                  textAlign: "left",
                  border: "none",
                  borderRadius: 8,
                  padding: "var(--space-2)",
                  // Buttons default to UA `buttontext` (black) — inherit the
                  // themed text color or dark mode goes black-on-black.
                  color: "inherit",
                  font: "inherit",
                  cursor: notification.readAt ? "default" : "pointer",
                  background: notification.readAt
                    ? "transparent"
                    : "var(--accent-soft)",
                }}
              >
                <div className="day-shifts-row-head">
                  <strong>{notification.title}</strong>
                  <span className="muted">{timeLabel(notification.createdAt)}</span>
                </div>
                <p style={{ margin: 0 }}>{notification.body}</p>
                {notification.payload?.carrying?.length ? (
                  <p className="muted" style={{ margin: 0 }}>
                    {notification.payload.orderCode
                      ? `${notification.payload.orderCode} · `
                      : ""}
                    pickup {notification.payload.pickupAddress}
                  </p>
                ) : null}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
